import React from 'react';
import { Award, Sparkles, Eye, MapPin, Cpu, BadgePoundSterling, ShieldCheck, Clock } from 'lucide-react';
import { PageHero, FadeIn, SectionLabel } from '../components/Shared';
import NeuralNetworkBg from '../components/bg/NeuralNetworkBg';

const About = () => {
    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="about-page">
            <PageHero
                label="About"
                title="About REVOLO AI WEB."
                subtitle="A UK-based agency at the intersection of web development and AI-powered security."
                testId="about-hero"
                bg={<NeuralNetworkBg />}
            />

            {/* Story */}
            <section className="py-20 bg-white" data-testid="about-story">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Our story</SectionLabel>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <p className="mt-6 font-display text-2xl sm:text-3xl md:text-[34px] font-semibold tracking-tight text-[#1A1A2E] leading-[1.3] text-balance">
                            REVOLO AI WEB was founded with a simple idea: building a beautiful
                            website isn't enough. In today's digital landscape, every business
                            website needs <span className="text-[#7B61FF]">ongoing protection</span>.
                            We combine expert web development with cutting-edge AI security tools
                            to give UK businesses a complete online solution — build, launch, and
                            protect.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Values */}
            <section className="py-20 bg-[#F7F7F8]" data-testid="about-values">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Our values</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-3xl leading-[1.15]">
                            How we work.
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Award,
                                title: 'Quality Over Quantity',
                                desc: 'We take on a limited number of projects to ensure every client gets our full attention and the best possible result.',
                            },
                            {
                                icon: Sparkles,
                                title: 'AI-Enhanced, Human-Led',
                                desc: 'We use AI tools to enhance our work — faster audits, smarter code analysis, better results. But every decision is made by our expert team.',
                            },
                            {
                                icon: Eye,
                                title: 'Transparency Always',
                                desc: "No jargon, no hidden fees, no vague promises. You see exactly what you're paying for, every month.",
                            },
                        ].map((v, i) => (
                            <FadeIn key={i} delay={i * 0.1}>
                                <div
                                    className="p-7 bg-white border border-[#E8E8EC] rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 h-full"
                                    data-testid={`about-value-${i + 1}`}
                                >
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#F2F0FE]">
                                        <v.icon className="w-5 h-5 text-[#7B61FF]" />
                                    </div>
                                    <h3 className="mt-5 font-display text-[19px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {v.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] text-[#4A4A5A] leading-relaxed">
                                        {v.desc}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-white" data-testid="about-why-us">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Why choose us</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-3xl leading-[1.15]">
                            Built for UK businesses.
                        </h2>
                    </FadeIn>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {[
                            { icon: MapPin, label: 'UK-based team' },
                            { icon: Cpu, label: 'AI-powered workflows' },
                            { icon: BadgePoundSterling, label: 'Fixed, transparent pricing' },
                            { icon: ShieldCheck, label: 'Monthly security included' },
                            { icon: Eye, label: 'GDPR compliant by default' },
                            { icon: Clock, label: 'Fast turnaround times' },
                        ].map((f, i) => (
                            <FadeIn key={i} delay={(i % 3) * 0.08}>
                                <div
                                    className="bg-white border border-[#E8E8EC] rounded-2xl p-6 h-full flex items-center gap-4"
                                    data-testid={`about-feature-${i + 1}`}
                                >
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#E6FBF6] shrink-0">
                                        <f.icon className="w-5 h-5 text-[#00B89E]" />
                                    </span>
                                    <span className="font-display text-[16px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {f.label}
                                    </span>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default About;
